import { useEffect, useId, useState } from 'react';
import { parseDecimal } from '@kaba/shared';
import { cx } from './ui';

/** Zahl deutsch mit Tausenderpunkten zeigen – so sieht man, was gelesen wurde („1.000“ = tausend) */
const shown = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(n) ? '' : n.toLocaleString('de-DE', { maximumFractionDigits: 10 }));

/**
 * 0.85 (N03): Gemeinsame Zahleneingabe für Dialoge (Summenlimit, Zielwert, Freigabegrenze, Regelwerte).
 * Liest wie die Karteneingabe über `parseDecimal` („1.000“, „1.000,50“, „1000.5“, „-3“), zeigt nach dem
 * Verlassen den verstandenen Wert formatiert. Ungültiges bleibt stehen und wird erklärt – nie still umgedeutet.
 */
export function DecimalInput({
  value,
  onChange,
  min,
  max,
  integer,
  allowEmpty = true,
  className,
  id,
  placeholder,
  'aria-label': ariaLabel,
}: {
  value: number | null | undefined;
  onChange: (n: number | null) => void;
  min?: number;
  max?: number;
  integer?: boolean;
  allowEmpty?: boolean;
  className?: string;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
}) {
  const [text, setText] = useState(shown(value));
  const [focused, setFocused] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const hint = useId();
  useEffect(() => {
    if (!focused && !problem) setText(shown(value));
  }, [value, focused, problem]);

  const check = (raw: string): { n: number | null; problem: string | null } => {
    if (!raw.trim()) return allowEmpty ? { n: null, problem: null } : { n: null, problem: 'Bitte eine Zahl eingeben.' };
    const n = parseDecimal(raw);
    if (n === null) return { n: null, problem: 'Keine Zahl – z. B. 1.000 oder 1.000,50' };
    if (integer && !Number.isInteger(n)) return { n: null, problem: 'Bitte eine ganze Zahl.' };
    if (min !== undefined && n < min) return { n: null, problem: `Mindestens ${shown(min)}.` };
    if (max !== undefined && n > max) return { n: null, problem: `Höchstens ${shown(max)}.` };
    return { n, problem: null };
  };

  return (
    <span className="inline-flex min-w-0 flex-col gap-0.5">
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={ariaLabel}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? hint : undefined}
        placeholder={placeholder}
        className={cx(className, problem && 'border-rose-500')}
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          setText(e.target.value);
          const r = check(e.target.value);
          setProblem(r.problem);
          if (!r.problem) onChange(r.n);
        }}
        onBlur={() => {
          setFocused(false);
          // Gültiges formatiert zeigen (was KaBa verstanden hat); Ungültiges bleibt zum Korrigieren stehen
          if (!problem) setText(shown(check(text).n));
        }}
      />
      {problem && (
        <span id={hint} className="text-xs text-rose-600">
          {problem}
        </span>
      )}
    </span>
  );
}
